import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '@/components/cn';

const svgProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Primary destinations shown as tabs (the raised centre tab is the add action). */
const TABS = [
  {
    to: '/',
    label: 'Home',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" {...svgProps}>
        <path d="M3 11l9-8 9 8M5 10v10h14V10" />
      </svg>
    ),
  },
  {
    to: '/assets',
    label: 'Assets',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" {...svgProps}>
        <rect x="3" y="5" width="18" height="3" rx="1.5" />
        <rect x="3" y="11" width="13" height="3" rx="1.5" />
        <rect x="3" y="17" width="8" height="3" rx="1.5" />
      </svg>
    ),
  },
  {
    to: '/assets?scan=1',
    label: '',
    primary: true,
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        aria-hidden
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
    ),
  },
  {
    to: '/insights',
    label: 'Insights',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" {...svgProps}>
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </svg>
    ),
  },
];

/** Secondary destinations, reachable on mobile through the Tools menu. */
const TOOLS = [
  {
    to: '/rentvest',
    label: 'Rentvest',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" {...svgProps}>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    to: '/fire',
    label: 'FIRE',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" {...svgProps}>
        <path d="M14.5 2C13 6 10 9 10 13a4 4 0 0 0 8 0c0-4-2-7-3.5-11z" />
        <path d="M13 22c-2 0-3.5-1-4.5-3" />
        <path d="M13 22c2 0 3.5-1 4.5-3" />
        <path d="M8 18c-3-1-4-3-4-5" />
        <path d="M18 18c3-1 4-3 4-5" />
      </svg>
    ),
  },
  {
    to: '/rebalance',
    label: 'Rebalance',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" {...svgProps}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a10 10 0 0 1 7 17H5A10 10 0 0 1 12 2z" />
        <path d="M12 2v20" />
        <path d="M2 12h20" />
      </svg>
    ),
  },
];

const TOOL_PATHS = TOOLS.map((t) => t.to);

/**
 * Mobile bottom tab bar — hidden at md+. The centre tab is a raised full-circle
 * Add button matching the design's `tab-primary` treatment. The Tools tab opens
 * a popover linking to the secondary destinations that live in the desktop
 * sidebar. Safe-area inset keeps it clear of the iOS home indicator.
 */
export const BottomNav = () => {
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsActive = TOOL_PATHS.includes(location.pathname);

  // Close on navigation and on Escape.
  useEffect(() => {
    setToolsOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!toolsOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setToolsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  return (
    <>
      {toolsOpen && (
        <button
          type="button"
          aria-label="Close tools menu"
          tabIndex={-1}
          className="fixed inset-0 z-40 bg-ink/10 md:hidden"
          onClick={() => {
            setToolsOpen(false);
          }}
        />
      )}
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-whisper bg-warm-white/90 pb-safe-bottom backdrop-blur-sm md:hidden"
      >
        {toolsOpen && (
          <div
            role="menu"
            aria-label="Tools"
            className="absolute bottom-full right-2 z-50 mb-2 w-52 overflow-hidden rounded-card border border-whisper bg-warm-white p-2 shadow-deep"
          >
            {TOOLS.map((tool) => (
              <NavLink
                key={tool.to}
                to={tool.to}
                role="menuitem"
                onClick={() => {
                  setToolsOpen(false);
                }}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-input px-3 py-2.5 text-[14px] font-[600] transition-colors',
                    isActive ? 'bg-ink text-warm-white' : 'text-ink hover:bg-ink/[0.05]',
                  )
                }
              >
                <span aria-hidden>{tool.icon}</span>
                {tool.label}
              </NavLink>
            ))}
          </div>
        )}

        <ul className="mx-auto flex max-w-lg items-end justify-around px-2">
          {TABS.map((tab) =>
            tab.primary ? (
              /* Raised + circle button */
              <li key={tab.to} className="flex-1">
                <NavLink
                  to={tab.to}
                  aria-label="Add asset"
                  className="flex h-16 flex-col items-center justify-center"
                >
                  {({ isActive }) => (
                    <span
                      className={cn(
                        '-mt-5 flex h-14 w-14 items-center justify-center rounded-full shadow-deep transition-transform active:scale-95',
                        isActive ? 'bg-lav' : 'bg-ink text-warm-white',
                      )}
                    >
                      {tab.icon}
                    </span>
                  )}
                </NavLink>
              </li>
            ) : (
              <li key={tab.to} className="flex-1">
                <NavLink
                  to={tab.to}
                  end={tab.to === '/'}
                  className="flex h-16 flex-col items-center justify-center gap-1"
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'transition-colors duration-150',
                          isActive ? 'text-ink' : 'text-warm-300',
                        )}
                      >
                        {tab.icon}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] font-semibold tracking-wide transition-colors duration-150',
                          isActive ? 'text-ink' : 'text-warm-300',
                        )}
                      >
                        {tab.label}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ),
          )}

          {/* Tools — opens the secondary destinations as a popover. */}
          <li className="flex-1">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={toolsOpen}
              aria-label="Tools"
              onClick={() => {
                setToolsOpen((open) => !open);
              }}
              className="flex h-16 w-full flex-col items-center justify-center gap-1"
            >
              <span
                className={cn(
                  'transition-colors duration-150',
                  toolsActive || toolsOpen ? 'text-ink' : 'text-warm-300',
                )}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" {...svgProps}>
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              </span>
              <span
                className={cn(
                  'text-[10px] font-semibold tracking-wide transition-colors duration-150',
                  toolsActive || toolsOpen ? 'text-ink' : 'text-warm-300',
                )}
              >
                Tools
              </span>
            </button>
          </li>
        </ul>
      </nav>
    </>
  );
};
